import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { teamAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const OffboardWizard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const companyId = user?.company_id;
  const [step, setStep] = useState(1);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [removedName, setRemovedName] = useState('');

  useEffect(() => {
    if (companyId) loadUsers();
  }, [companyId]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const res = await teamAPI.listUsers(companyId);
      setUsers(res.users || []);
    } catch (err) {
      setError('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(u => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (u.firstname + ' ' + u.lastname).toLowerCase().includes(term) ||
      u.email.toLowerCase().includes(term)
    );
  });

  const handleRemove = async () => {
    if (!selectedUser) return;
    try {
      setProcessing(true);
      setError('');
      await teamAPI.removeUser(companyId, selectedUser.id);
      setRemovedName(`${selectedUser.firstname} ${selectedUser.lastname}`);
      setSuccess(true);
    } catch (err) {
      setError(err.error || err.message || 'Failed to remove team member');
    } finally {
      setProcessing(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center py-12 px-4">
        <div className="max-w-md w-full text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
            <span className="text-3xl">✅</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Team member removed</h2>
          <p className="text-gray-600 mb-8">
            {removedName} has been removed from your company. They can no longer access your company's applications.
          </p>
          <button
            onClick={() => navigate('/tasks')}
            className="px-6 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-hover text-sm font-medium"
          >
            Back to team tasks
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <button onClick={() => step > 1 ? setStep(step - 1) : navigate('/tasks')} className="text-gray-600 text-sm mb-6 hover:text-gray-900">
          ← {step > 1 ? 'Back' : 'Back to team tasks'}
        </button>

        <h1 className="text-2xl font-bold text-gray-900 mb-2">Remove a team member</h1>

        <div className="flex items-center gap-2 mb-8">
          {[1, 2].map(s => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                s < step ? 'bg-primary text-white' :
                s === step ? 'bg-primary text-white ring-4 ring-primary-light' :
                'bg-gray-200 text-gray-500'
              }`}>
                {s < step ? '✓' : s}
              </div>
              <span className={`text-sm ${s <= step ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
                {s === 1 ? 'Select person' : 'Confirm'}
              </span>
              {s < 2 && <div className={`w-8 h-0.5 ${s < step ? 'bg-primary' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
        )}

        {/* Step 1: Select person */}
        {step === 1 && (
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Who is leaving?</h2>
            <p className="text-sm text-gray-500 mb-4">Select the team member you want to remove from your company.</p>

            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary text-sm mb-4"
            />

            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading team members...</div>
            ) : (
              <div className="space-y-1 max-h-80 overflow-y-auto">
                {filteredUsers.map(user => (
                  <div
                    key={user.id}
                    onClick={() => { setSelectedUser(user); setStep(2); }}
                    className={`flex items-center justify-between px-4 py-3 rounded-lg cursor-pointer transition-colors ${
                      selectedUser?.id === user.id
                        ? 'bg-primary-light/10 border border-primary/20'
                        : 'hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <div>
                      <div className="font-medium text-gray-900 text-sm">
                        {user.firstname} {user.lastname}
                      </div>
                      <div className="text-xs text-gray-500">{user.email}</div>
                    </div>
                    <span className="text-gray-400 text-sm">Select →</span>
                  </div>
                ))}
                {filteredUsers.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-4">
                    {searchTerm ? 'No team members match your search.' : 'No team members found.'}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Confirm */}
        {step === 2 && selectedUser && (
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Confirm removal</h2>
            <p className="text-sm text-gray-500 mb-6">
              Review what happens before removing this person from your company.
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-medium text-amber-800 mb-2">What happens when you remove them:</h3>
              <ul className="text-sm text-amber-700 space-y-1.5">
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">•</span>
                  <span><strong>{selectedUser.firstname} {selectedUser.lastname}</strong> will no longer be able to sign in to your company's applications.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">•</span>
                  <span>Their account is not deleted — they can still access other companies they belong to.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">•</span>
                  <span>You can invite them again later if needed.</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="text-sm text-gray-500">Removing:</div>
              <div className="font-medium text-gray-900">
                {selectedUser.firstname} {selectedUser.lastname}
              </div>
              <div className="text-sm text-gray-500">{selectedUser.email}</div>
            </div>

            <div className="flex justify-between">
              <button onClick={() => setStep(1)} className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium">
                Back
              </button>
              <button
                onClick={handleRemove}
                disabled={processing}
                className="px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm font-medium"
              >
                {processing ? 'Removing...' : `Remove ${selectedUser.firstname} from company`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OffboardWizard;
